/**
 * NFC 管理服务
 * NFC Management Service
 *
 * 调用云端 Admin Portal API 的 NFC 管理接口
 */

import { adminPortalApi as apiClient } from './api';

// ============================================================
// 类型定义
// ============================================================

export type EntityType = 'slot' | 'figure' | 'card' | 'prop';
export type NfcStatus = 'active' | 'retired';
export type NfcSource = 'venue' | 'merch' | 'ops';
export type ScanResult = 'success' | 'miss' | 'rejected' | 'error';
export type ScanSource = 'cloud_api' | 'local_mqtt';

// NFC 身份注册
export interface NfcIdentityRegistry {
  id: number;
  entity_type: EntityType;
  entity_id: string;
  display_name: string;
  identity_desc?: string;
  source: NfcSource;
  status: NfcStatus;
  extra_metadata: Record<string, any>;
  is_deleted: boolean;
  retired_at?: string;
  deleted_at?: string;
  created_at: string;
  updated_at: string;
}

export interface NfcIdentityRegistryCreate {
  entity_type: EntityType;
  entity_id: string;
  display_name: string;
  identity_desc?: string;
  source?: NfcSource;
  extra_metadata?: Record<string, any>;
}

export interface NfcIdentityRegistryUpdate {
  display_name?: string;
  identity_desc?: string;
  status?: NfcStatus;
  extra_metadata?: Record<string, any>;
}

// NFC 扫描日志
export interface NfcScanLog {
  id: number;
  world_id: string;
  device_id?: string;
  player_id: string;
  entity_type: EntityType;
  entity_id: string;
  matched_binding_id?: number;
  result: ScanResult;
  reason?: string;
  source: ScanSource;
  scanned_at: string;
  is_deleted: boolean;
  deleted_at?: string;
  created_at: string;
  updated_at: string;
}

export interface NfcScanLogCreate {
  world_id: string;
  device_id?: string;
  player_id: string;
  entity_type: EntityType;
  entity_id: string;
  matched_binding_id?: number;
  result: ScanResult;
  reason?: string;
  source?: ScanSource;
  scanned_at?: string;
}

// NFC 扫描统计
export interface NfcScanStatistics {
  total: number;
  success_count: number;
  miss_count: number;
  rejected_count: number;
  error_count: number;
  success_rate: number;
}

// NFC 绑定规则
export interface NfcBinding {
  id: number;
  config_key: string;
  config_name: string;
  config_data: Record<string, any>;
  sort_order: number;
}

// NFC 物料清单项
export interface NfcRequirement {
  id: number;
  config_key: string;
  config_name: string;
  config_data: Record<string, any>;
  sort_order: number;
}

// 分页响应
export interface PaginatedResponse<T> {
  total: number;
  page: number;
  page_size: number;
  items: T[];
}

// ============================================================
// API 函数
// ============================================================

/**
 * 获取 NFC 身份列表
 */
export const getIdentityList = async (params: {
  page?: number;
  page_size?: number;
  entity_type?: EntityType;
  status?: NfcStatus;
  search?: string;
  include_deleted?: boolean;
}): Promise<PaginatedResponse<NfcIdentityRegistry>> => {
  const response = await apiClient.get('/nfc/registry', { params });
  return response.data;
};

/**
 * 获取单个 NFC 身份详情
 */
export const getIdentityById = async (id: number): Promise<NfcIdentityRegistry> => {
  const response = await apiClient.get(`/nfc/registry/${id}`);
  return response.data;
};

/**
 * 创建 NFC 身份
 */
export const createIdentity = async (data: NfcIdentityRegistryCreate): Promise<NfcIdentityRegistry> => {
  const response = await apiClient.post('/nfc/registry', data);
  return response.data;
};

/**
 * 更新 NFC 身份
 */
export const updateIdentity = async (id: number, data: NfcIdentityRegistryUpdate): Promise<NfcIdentityRegistry> => {
  const response = await apiClient.put(`/nfc/registry/${id}`, data);
  return response.data;
};

/**
 * 删除 NFC 身份（软删除）
 */
export const deleteIdentity = async (id: number): Promise<void> => {
  await apiClient.delete(`/nfc/registry/${id}`);
};

/**
 * 停用 NFC 身份
 */
export const retireIdentity = async (id: number): Promise<void> => {
  await apiClient.post(`/nfc/registry/${id}/retire`);
};

/**
 * 恢复已删除的 NFC 身份
 */
export const restoreIdentity = async (id: number): Promise<void> => {
  await apiClient.post(`/nfc/registry/${id}/restore`);
};

/**
 * 增量同步 NFC 身份列表
 */
export const syncIdentities = async (params: {
  since?: string;
  limit?: number;
}): Promise<{
  version: string;
  items: NfcIdentityRegistry[];
  has_more: boolean;
}> => {
  const response = await apiClient.get('/nfc/registry/sync', { params });
  return response.data;
};

/**
 * 获取 NFC 扫描日志列表
 */
export const getScanLogList = async (params: {
  page?: number;
  page_size?: number;
  world_id?: string;
  entity_type?: EntityType;
  source?: ScanSource;
  result?: ScanResult;
  start_time?: string;
  end_time?: string;
}): Promise<PaginatedResponse<NfcScanLog>> => {
  const response = await apiClient.get('/nfc/scan-logs', { params });
  return response.data;
};

/**
 * 创建扫描日志（云端直扫）
 */
export const createScanLog = async (data: NfcScanLogCreate): Promise<NfcScanLog> => {
  const response = await apiClient.post('/nfc/scan-logs', data);
  return response.data;
};

/**
 * 获取 NFC 扫描统计
 */
export const getScanStatistics = async (params: {
  world_id?: string;
  start_time?: string;
  end_time?: string;
}): Promise<NfcScanStatistics> => {
  const response = await apiClient.get('/nfc/scan-logs/statistics', { params });
  return response.data;
};

/**
 * 获取剧本 NFC 绑定规则
 */
export const getNfcBindings = async (params: {
  script_id: number;
  script_version?: string;
}): Promise<{
  script_id: number;
  script_version?: string;
  count: number;
  bindings: NfcBinding[];
}> => {
  const response = await apiClient.get('/nfc/bindings', { params });
  return response.data;
};

/**
 * 获取剧本 NFC 物料清单
 */
export const getNfcRequirements = async (params: {
  script_id: number;
  script_version?: string;
}): Promise<{
  script_id: number;
  script_version?: string;
  count: number;
  requirements: NfcRequirement[];
}> => {
  const response = await apiClient.get('/nfc/requirements', { params });
  return response.data;
};

// ============================================================
// 工具函数
// ============================================================

export const ENTITY_TYPE_LABELS: Record<EntityType, string> = {
  slot: '场馆固定槽位',
  figure: '玩家人偶',
  card: '玩家卡片',
  prop: '可移动道具',
};

export const NFC_STATUS_LABELS: Record<NfcStatus, string> = {
  active: '激活',
  retired: '停用',
};

export const NFC_SOURCE_LABELS: Record<NfcSource, string> = {
  venue: '场馆',
  merch: '商品发行',
  ops: '运营',
};

export const SCAN_RESULT_LABELS: Record<ScanResult, string> = {
  success: '成功',
  miss: '未匹配',
  rejected: '拒绝',
  error: '错误',
};

export const SCAN_SOURCE_LABELS: Record<ScanSource, string> = {
  cloud_api: '云端 API',
  local_mqtt: '本地 MQTT',
};
